import type {ReactNode} from 'react'

import {useAppDispatch, useAppSelector} from '../../../../../../hooks/react-redux'
import {collapsedBlocks} from '../../../EditPipelinePage/slices/EditPipelinePage.slices'
import useUID from '../../../hooks/useUID'

import SettingsBlockHeader from './SettingsBlockHeader/SettingsBlockHeader'

import styles from './SettingsBlock.css'

type Props = {
  id: string
  title: string | null | undefined
  generatedTitle?: string | null | undefined
  children?: ReactNode
  collapsible?: boolean
  collapsedDefault?: boolean
  collapsedNote?: ReactNode
  isDeleted?: boolean
  details?: ReactNode
  rightElement?: ReactNode | ((editing: boolean) => ReactNode)
  onChangeName?: (name: string) => void
  onDeleteClick?: () => void
  onRestoreClick?: () => void
  onAddClick?: () => void
  updated?: boolean
  customActions?: (anchor: ReactNode) => ReactNode
  secondLine?: ReactNode
  draggable?: boolean
}

function SettingsBlock({
  id,
  collapsedDefault,
  title,
  collapsible,
  children,
  generatedTitle,
  isDeleted,
  draggable,
  ...props
}: Props) {
  const collapsed = useAppSelector(state => state.pipelines.collapsedBlocks[id] ?? collapsedDefault)
  const dispatch = useAppDispatch()
  const actuallyCollapsed = isDeleted || collapsed
  const regionId = useUID(`step-${title || generatedTitle}`)
  const buttonId = useUID(`button-${title}`)

  return (
    <div className={styles.section}>
      <SettingsBlockHeader
        title={title}
        generatedTitle={generatedTitle}
        collapsed={actuallyCollapsed}
        isDeleted={isDeleted}
        collapse={() => dispatch(collapsedBlocks.actions.toggle({id, collapsed: true}))}
        collapsible={collapsible}
        buttonId={buttonId}
        expand={() => dispatch(collapsedBlocks.actions.toggle({id, collapsed: false}))}
        regionId={regionId}
        draggable={draggable}
        {...props}
      />
      {collapsible && (
        <div
          role="region"
          id={regionId}
          hidden={actuallyCollapsed}
          aria-labelledby={buttonId}
          className={styles.collapsibleRegion}
        >
          {!actuallyCollapsed && children}
        </div>
      )}
    </div>
  )
}

export default SettingsBlock
